import { Campaign, CustomField } from "@/types/djangio"
import React, { useMemo, useState } from "react"
import { Contents } from "QuorumGrassroots/campaign-forms/components/PersonalizedMessages/PersonalizedMessages"
import { PromptAndResponseCampaignForm } from "QuorumGrassroots/campaign-forms/components/PersonalizedMessages/PersonalizedMessages.helper"
import { useFormReturn } from "QuorumGrassroots/campaign-forms/hooks/useForm"
import { GenericWriteForm } from "QuorumGrassroots/campaign-forms/components/ReusableForms/GenericWriteForm"
import {
    patchGrassrootsAction,
    updateSupporterHasParticipatedInCampaigns,
    usePersonalizedCampaignGetCampaignMessages,
    useReworkMessageWithAI,
} from "QuorumGrassroots/services"
import { isFeatureEnabled } from "shared/featureflags/helperFunctions"
import {
    adaptTargetedMessages,
    adaptTargetedMessagesToActionsGenerator,
    getPointsForCampaign,
} from "QuorumGrassroots/campaign-forms/helpers"
import { generatePersonalizedMessagesActions } from "QuorumGrassroots/campaign-forms/grassrootsActionGenerators"
import { useMutation } from "@tanstack/react-query"
import BACKENDERROR from "app/static/frontend/imports/backenderror"
import { swalConfigs } from "QuorumGrassroots/swalConfigs"
import { getPathObject, runUserJavascript } from "QuorumGrassroots/helperFunctions"
import { useNavigate } from "react-router-dom"
import { ThanksDisplay } from "QuorumGrassroots/widgets/ReusableComponents/ThanksDisplay"
import { getReduxlessThanksDisplayProps } from "QuorumGrassroots/widgets/ReusableComponents/WidgetWrappers/ThankableWrapper"
import { useGrassrootsActionCenterSettings } from "QuorumGrassroots/services/grassrootsActionCenterSettings"
import { withGamificationModal } from "QuorumGrassroots/campaign-forms/wrappers/withGamificationModal"
import { Container } from "QuorumGrassroots/campaign-forms/components/PersonalizedMessages/components/QuestionsContainer.styles"
import { Heading } from "QuorumGrassroots/campaign-forms/components/PersonalizedMessages/PersonalizedMessages.styles"

interface Props {
    form: useFormReturn<PromptAndResponseCampaignForm>
    questions: CustomField[]
    handleChangeContent: (contentName: Contents) => void
    campaign: Campaign
    isEmbedded: boolean
    setIsFinished: (value: boolean) => void
    showGamificationLevelUpModalIfEnabled: (pointsEarned?: number) => void
    pointsEarned?: number
}

const { CampaignType } = DjangIO.app.grassroots.campaign.types

const addRequiredValuesToActions = (actions: object[], campaign: Campaign) => {
    return actions.map((action) => ({
        ...action,
        supporter: DjangIO.app.grassroots.models.Supporter.resourceUriFromId(window.userdata.id),
        organization: DjangIO.app.userdata.models.Organization.resourceUriFromId(window.organization.id),
        action_center: window.action_center_settings.resource_uri,
        points_earned: getPointsForCampaign(campaign),
        campaign: DjangIO.app.grassroots.campaign.models.Campaign.resourceUriFromId(campaign.id),
    }))
}

export const submitAction = async (
    campaign: Campaign,
    messages: Record<string, any>,
    targets: object,
    tag_dict,
    extraMessageData?: Record<string, any>,
) => {
    // Ensure variation_id on each message.raw using extra_message_data as fallback (flag-gated)
    const messagesWithVariation = isFeatureEnabled("ff_grassroots_edited_content_filter")
        ? Object.fromEntries(
              Object.entries(messages || {}).map(([groupId, message]: [string, any]) => {
                  const vid =
                      message?.raw?.variation_id ??
                      message?.variation_id ??
                      extraMessageData?.[groupId]?.raw?.variation_id ??
                      null
                  return [
                      groupId,
                      {
                          ...message,
                          raw: {
                              ...message?.raw,
                              variation_id: vid,
                          },
                      },
                  ]
              }),
          )
        : messages

    // Inject immutable originals for edited-content tracking using extra_message_data when available
    const messagesWithOriginals = isFeatureEnabled("ff_grassroots_edited_content_filter")
        ? Object.fromEntries(
              Object.entries(messagesWithVariation || {}).map(([groupId, message]: [string, any]) => {
                  const extra = (extraMessageData as any)?.[groupId]
                  const pre = extra?.raw?.fixed_pre_message ?? message?.raw?.preBody ?? ""
                  const body = extra?.raw?.content ?? message?.raw?.content ?? ""
                  const post = extra?.raw?.fixed_post_message ?? message?.raw?.postBody ?? ""
                  const original = [pre, body, post].filter(Boolean).join("\n\n")
                  return [
                      groupId,
                      {
                          ...message,
                          // store both combined and split originals; helpers will prefer these if present
                          original_template_text: message?.original_template_text ?? original,
                          original_prebody: pre,
                          original_body: body,
                          original_postbody: post,
                      },
                  ]
              }),
          )
        : messagesWithVariation

    const messagesForAdapt = messagesWithOriginals

    const { adaptedMessages, messageGroups } = isFeatureEnabled("ff_ngg_message_preview")
        ? adaptTargetedMessages(messagesForAdapt, targets)
        : adaptTargetedMessagesToActionsGenerator(messagesForAdapt, targets)

    const actions = generatePersonalizedMessagesActions(adaptedMessages, {}, { campaign, messageGroups, tag_dict })
    const actionsWithRequiredValues = addRequiredValuesToActions(actions, campaign)
    await patchGrassrootsAction({ objects: actionsWithRequiredValues })
}

export const SendMessageContainer = withGamificationModal((props: Props) => {
    const placeholders = props.form.values
    const navigate = useNavigate()
    const actionCenterSettings = useGrassrootsActionCenterSettings(props.campaign.action_center_id)
    const messagesQuery = usePersonalizedCampaignGetCampaignMessages(String(props.campaign.id))

    const { data: parsedMessages } = useReworkMessageWithAI(
        messagesQuery.data,
        placeholders,
        props.questions,
        props.campaign,
    )

    const messageData = useMemo(() => messagesQuery.data, [messagesQuery.data]) as {
        messages: object
        targets: object
        extra_message_data?: Record<string, any>
    }

    const [messages, setMessages] = useState({ [CampaignType.personalized_messages.value]: parsedMessages })

    const [targets, setTargets] = useState({ [CampaignType.personalized_messages.value]: messageData.targets })
    const [isLoading, setIsLoading] = useState(false)
    const [isFinished, setIsFinished] = useState(false)
    const tag_dict = props.form.values

    const submitMutation = useMutation<void, unknown, Campaign>({
        mutationFn: async (values) => {
            setIsLoading(true)
            await submitAction(
                values,
                messages[CampaignType.personalized_messages.value],
                targets[CampaignType.personalized_messages.value],
                tag_dict,
                messageData?.extra_message_data as any,
            ).finally(() => setIsLoading(false))
        },
        onError: (error) => {
            BACKENDERROR(error, swalConfigs.postGrassrootsActionError, true, false)
            throw error
        },
        onSuccess: () => handleSuccess(),
    })

    const handleSuccess = () => {
        props.showGamificationLevelUpModalIfEnabled()
        runUserJavascript(props.campaign.custom_thank_you_javascript)
        const redirectDetails =
            props.campaign.success_redirection_url && getPathObject(props.campaign.success_redirection_url)
        if (!props.isEmbedded && redirectDetails) {
            if (redirectDetails.isInternal) {
                navigate(redirectDetails.url)
            } else {
                window.swal({ icon: "success", title: "Thank you! You are now being redirected..." })
                window.location.assign(redirectDetails.url)
            }
        } else {
            setIsFinished(true)
        }
        updateSupporterHasParticipatedInCampaigns()
    }

    if (isFinished) {
        const pointsEarned = getPointsForCampaign(props.campaign)
        const thankableProps = getReduxlessThanksDisplayProps(
            actionCenterSettings,
            props.campaign,
            props.isEmbedded,
            pointsEarned,
        )

        return <ThanksDisplay {...thankableProps} />
    }

    return (
        <Container isSingleColumnLayout>
            <Heading>
                {props.campaign.custom_call_to_action_text ? (
                    <h3>{props.campaign.custom_call_to_action_text}</h3>
                ) : (
                    <>
                        <h3>Act now!</h3>
                        <h5>Fill out the form below to support us!</h5>
                    </>
                )}
            </Heading>
            <GenericWriteForm
                {...props}
                campaign={{ ...props.campaign, messages: messages[CampaignType.personalized_messages.value] }}
                queriedMessages={messages}
                setQueriedMessages={setMessages}
                queriedTargets={targets}
                customSubmit={submitMutation.mutate}
                setQueriedTargets={setTargets}
                isQueryMode
                isSingleColumnLayout
                isLoading={isLoading}
                submitButtonTextKey="campaign.personalized_messages.submit_verb"
                submitButtonClassName="custom-submit-button-class"
                showAIDisclaimer={props.campaign.should_rework_with_ai}
            />
        </Container>
    )
})
